import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Export from "../../../../formHelper/export";
import exportApiCall from "../../../../apiHelper/export";
import TravelDetails from "./travelDetails";
import CustomerOrder from "./order";
import BillPayment from "./billPayment";
import ReportDetails from "./report";
import WrappedExpenseHistoryPage from "./history";
import Customer from "./company";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import Success from "../../../../formHelper/success";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../core/data/redux/store";

type TabKey =
  | "company"
  | "travel"
  | "customer_order"
  | "visit"
  | "advance_payment"
  | "report"
  | "history";

interface IdName {
  id: number;
  name: string;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

interface CompanyFormData {
  company_name: string;
  customer_name: string;
  owner_id: number | null;
  classification_id: number | null;
  mobile_no: string;
  landline_no: string;
  address: string;
  pin_code: string;
  city: string;
  country_id: number | null;
  state_id: number | null;
  other_state: string;
  email_id: string;
  gst_number: string;
  gst_field: boolean;
  state_dd: string;
}

const CompanySettings: React.FC = () => {
  const [showTab, setShowTab] = useState<TabKey>("company");
  const [companyKey, setCompanyKey] = useState(0);
  const backendRoute = all_routes.backend;

  const profile = useSelector((s: RootState) => s.profile?.data);

  // sidebar items: ensure keys are valid TabKey and conditionally include 'report'
  const sidebarItems = useMemo(
    () =>
      (
        [
          {
            key: "company" as TabKey,
            label: "Company",
            icon: "ti ti-building",
          },
          {
            key: "travel" as TabKey,
            label: "Travel & Expenses",
            icon: "ti ti-map-pin-check",
          },
          {
            key: "customer_order" as TabKey,
            label: "Purpose of Visit",
            icon: "ti ti-user-check",
          },
          {
            key: "advance_payment" as TabKey,
            label: "Bill & Payment",
            icon: "ti ti-credit-card",
          },
        ] as { key: TabKey; label: string; icon: string }[]
      ).concat(
        profile?.team_type === "service"
          ? [
              {
                key: "report" as TabKey,
                label: "Service Report",
                icon: "ti ti-book",
              },
            ]
          : []
      ),
    [profile?.team_type]
  );

  const sidebarBaseStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "10px 14px",
    fontWeight: 500,
    color: "#6c757d",
    background: "transparent",
    border: "none",
    borderLeft: "3px solid transparent",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  const activeStyle: React.CSSProperties = {
    color: "#e41f07",
    background: "#fce9e6",
    borderLeft: "3px solid #e41f07",
  };

  // exportFile: ensure we use result.data (blob) and revoke URL
  const exportFile = useCallback(async () => {
    try {
      const result = await exportApiCall("post", "app", {
        data: {},
        responseType: "blob",
      });

      // exportApiCall should return response where .data is the Blob (adjust if your helper returns differently)
      const blobData = (result && (result as any).data) || result;
      const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `brand-${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
    }
  }, []);

  const [formData, setFormData] = useState<CompanyFormData>({
    company_name: "",
    customer_name: "",
    owner_id: null,
    classification_id: null,
    mobile_no: "",
    landline_no: "",
    address: "",
    pin_code: "",
    city: "",
    country_id: null,
    state_id: null,
    other_state: "",
    email_id: "",
    gst_number: "",
    gst_field: false,
    state_dd: "",
  });

  const [formError, setFormError] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [tabsDisabled, setTabsDisabled] = useState<boolean>(true);

  // NEW: id to pass to Customer for editing
  const [selectedEditId, setSelectedEditId] = useState<number | null>(null);

  const countryList: IdName[] = useMemo(
    () => [
      { id: 1, name: "India" },
      { id: 2, name: "USA" },
    ],
    []
  );
  const stateList: IdName[] = useMemo(
    () => [
      { id: 1, name: "Maharashtra" },
      { id: 2, name: "Karnataka" },
    ],
    []
  );

  const inputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormError({});
      const { name, value, type, checked } = e.target as HTMLInputElement;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    },
    []
  );                      

  const isHistory = showTab === "history";
  const isSalesTeam = !isHistory;

  const goToNextTab = useCallback(() => {                 
    const currentIndex = sidebarItems.findIndex((item) => item.key === showTab);
    if (currentIndex !== -1 && currentIndex < sidebarItems.length - 1) {
      setShowTab(sidebarItems[currentIndex + 1].key);
    }
  }, [showTab, sidebarItems]);

  const goToPreviousTab = useCallback(() => {
    const currentIndex = sidebarItems.findIndex((item) => item.key === showTab);
    if (currentIndex === -1) {
      console.warn("goToPreviousTab: current tab not found");
      return;
    }
    const prevItem = sidebarItems[currentIndex - 1];
    if (prevItem) setShowTab(prevItem.key);
    else {
      alert("Already at first tab");
    }
  }, [showTab, sidebarItems]);

  const addNewExpanses = useCallback(async () => {
    try {
      const res = await apiRequestCall("post", backendRoute.addNewExpanses);
      if (res?.data) {
        setShowTab("company");
        setShowSuccess(true);
        setShowMessage(res.data.message ?? "New expenses created");
        setCompanyKey((k) => k + 1);
        setSelectedEditId(null);
      } else {
        console.warn("addNewExpanses: unexpected response", res);
      }
    } catch (err) {
      console.error("Failed to create new expenses:", err);
    }
  }, [backendRoute.addNewExpanses]);

  const tryLoadCmp = useCallback(async (): Promise<
    number | null | undefined
  > => {
    try {
      const res = await apiRequestCall("post", backendRoute.getExpansesDetails);
      const data = res?.data?.data;
      if (!data) {
        setCompanyId(null);
        setTabsDisabled(true);
        return undefined;
      }
      const cid = data.company_id ?? null;
      setCompanyId(cid);
      setTabsDisabled(cid === null);
      return cid;
    } catch (err) {
      console.error("Failed to load company details:", err);
      setCompanyId(null);
      setTabsDisabled(true);
      return undefined;
    }
  }, [backendRoute.getExpansesDetails]);

  useEffect(() => {
    void tryLoadCmp();
  }, [tryLoadCmp]);

  const handleEditFromHistory = useCallback((id: string | number) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) {
      console.warn("handleEditFromHistory received invalid id:", id);
      return;
    }
    setCompanyKey((k) => k + 1);
    setSelectedEditId(numericId);
    setTabsDisabled(false);
    setShowTab("company");
  }, []);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-12 col-md-6">
                  <h4 className="page-title mb-3 mb-md-0">Expenses</h4>
                </div>
              </div>
            </div>

            <div className="card mb-2">
              <div className="card-body pb-0 pt-2">
                <div className="d-flex justify-content-between align-items-center">
                  {(permission?.role?.permissions?.expenses?.sale_team ||
                    permission?.role?.permissions?.expenses?.service_team) && (
                    <ul className="nav nav-tabs-bottom mb-2">
                      <li className="nav-item me-4">
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTab("company");
                            setSelectedEditId(null);
                          }}
                          className={`nav-link px-0 ${
                            isSalesTeam ? "active" : ""
                          }`}
                        >
                          <i className="ti ti-users" />{" "}
                          {permission?.role?.permissions?.expenses?.sale_team
                            ? "Sales Team"
                            : "Service Team"}
                        </Link>
                      </li>

                      {permission?.role?.permissions?.expenses
                        ?.view_history && (
                        <li className="nav-item me-4">
                          <Link
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setShowTab("history");
                              setSelectedEditId(null);
                            }}
                            className={`nav-link px-0 ${
                              isHistory ? "active" : ""
                            }`}
                          >
                            <i className="ti ti-history" /> Previous History
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}

                  {permission?.role?.permissions?.expenses
                    ?.add_new_expenses && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center mb-2"
                      aria-label="Add new owner"
                      onClick={addNewExpanses}
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {(permission?.role?.permissions?.expenses?.sale_team ||
              permission?.role?.permissions?.expenses?.service_team) && (
              <div className="row">
                {!isHistory && (
                  <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="mb-2" style={{ marginTop: "-5px" }}>
                          Raise Your Expenses
                        </h5>
                        <div className="list-group list-group-flush">
                          {sidebarItems.map((item) => {
                            const disabled = false;
                            // tabsDisabled && item.key !== "company";
                            return (
                              <button
                                key={item.key}
                                style={{
                                  ...sidebarBaseStyle,
                                  ...(showTab === item.key ? activeStyle : {}),
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  pointerEvents: disabled ? "none" : "auto",
                                  opacity: disabled ? 0.5 : 1,
                                }}
                                onClick={() => {
                                  if (!disabled) setShowTab(item.key);
                                }}
                                onMouseEnter={(e) => {
                                  if (!disabled && showTab !== item.key) {
                                    e.currentTarget.style.color = "#e41f07";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!disabled && showTab !== item.key) {
                                    e.currentTarget.style.color = "#6c757d";
                                  }
                                }}
                              >
                                <i
                                  className={item.icon}
                                  style={{ fontSize: "16px" }}
                                />{" "}
                                <span>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className={isHistory ? "col-12" : "col-xl-9 col-lg-12"}>
                  <div className="card">
                    <div className="card-body">
                      {showTab === "company" && (
                        <div>
                          <h6 className="text-xl font-semibold mb-1">
                            Fill Company Details
                          </h6>
                          <Customer
                            key={companyKey}
                            onNext={goToNextTab}
                            editId={selectedEditId}
                          />
                        </div>
                      )}

                      {showTab === "travel" && (
                        <div>
                          <h6 className="text-xl font-semibold mb-1">
                            Travel & Expenses
                          </h6>
                          <TravelDetails
                            key={companyKey}
                            onNext={goToNextTab}
                            onPrev={goToPreviousTab}
                          />
                        </div>
                      )}

                      {showTab === "customer_order" && (
                        <div>
                          <h6 className="text-xl font-semibold mb-1">
                            Link Received Orders
                          </h6>
                          <CustomerOrder
                            key={companyKey}
                            onNext={goToNextTab}
                            onPrev={goToPreviousTab}
                          />
                        </div>
                      )}

                      {showTab === "visit" && (
                        <div>
                          <h6 className="text-xl font-semibold mb-1">
                            Visit Details
                          </h6>
                          <label className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              name="email_notifications"
                              onChange={() => {}}
                            />
                            <span>Email Notifications</span>
                          </label>
                        </div>
                      )}

                      {showTab === "advance_payment" && (
                        <div>
                          <h6 className="text-xl font-semibold mb-1">
                            Bill & Payment Details
                          </h6>
                          <BillPayment
                            key={companyKey}
                            onNext={goToNextTab}
                            onPrev={goToPreviousTab}
                          />
                        </div>
                      )}

                      {showTab === "report" && (
                        <div>
                          <h6 className="text-xl font-semibold mb-1">
                            Service Reports
                          </h6>
                          <ReportDetails
                            onNext={goToNextTab}
                            onPrev={goToPreviousTab}
                          />
                        </div>
                      )}

                      {showTab === "history" && (
                        <div>
                          <WrappedExpenseHistoryPage
                            onEdit={handleEditFromHistory}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Success
        id="OwnerSuccessModal"
        title="You"
        message={showMessage}
        show={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          void tryLoadCmp().then((id) => {
            if (typeof id === "number" && id !== null) {
              setTabsDisabled(false);
            }
          });
        }}
      />
    </div>
  );
};

export default CompanySettings;
